import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Store, PauseCircle } from "lucide-react";
import BottomNav from "@/components/app/BottomNav";
import AdegaForm from "@/components/app/AdegaForm";
import ProductsManager from "@/components/app/ProductsManager";
import { fullUrl } from "@/components/app/ImageUploader";

function Header({ adega }) {
  return (
    <header className="px-6 pt-10 pb-5 flex items-center gap-4">
      <div className="h-16 w-16 rounded-full overflow-hidden border-[3px] border-primary bg-muted grid place-items-center shrink-0">
        {adega?.photo_url ? <img src={fullUrl(adega.photo_url)} alt="" className="w-full h-full object-cover" /> : <Store className="w-7 h-7 text-primary" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">Painel Sou Adega</div>
        <h1 className="text-3xl heading font-extrabold truncate" data-testid="adega-dashboard-title">{adega?.name || "Cadastre sua adega"}</h1>
        {adega && (
          <Badge className={adega.status === "active" && !adega.expired && adega.is_open ? "bg-emerald-500 text-black" : "bg-muted text-muted-foreground"} data-testid="adega-open-status">
            {adega.status === "pending" ? "Pendente" : adega.status === "paused" || adega.expired ? "Pausada" : adega.is_open ? "Aberta agora" : "Fechada"}
          </Badge>
        )}
      </div>
    </header>
  );
}

export default function AdegaDashboard() {
  const [adega, setAdega] = useState(undefined);
  const { checkAuth } = useAuth();
  const onSaved = (a) => { setAdega(a); checkAuth(); };

  useEffect(() => {
    api.get("/adegas/mine").then(({ data }) => setAdega(data)).catch(() => setAdega(null));
  }, []);

  if (adega === undefined) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando...</div>;

  return (
    <div className="min-h-[100dvh] pb-24 max-w-md mx-auto" data-testid="adega-dashboard">
      <Header adega={adega} />
      <section className="px-6">
        {adega?.status === "pending" && (
          <div className="rounded-xl border border-primary/50 bg-primary/10 p-3 text-sm mb-4" data-testid="adega-pending-warning">
            Cadastro recebido! Sua adega aparece no mapa assim que o admin aprovar. Enquanto isso, monte seu catálogo.
          </div>
        )}
        {adega?.status === "active" && adega.expired && (
          <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-3 text-sm mb-4" data-testid="adega-expired-warning">
            O período ativo da sua adega terminou e o pin foi pausado. Seu catálogo continua guardado.
          </div>
        )}
        {adega?.status === "paused" && (
          <div className="flex items-start gap-2 rounded-xl border border-destructive/50 bg-destructive/10 p-3 text-sm mb-4" data-testid="adega-paused-warning">
            <PauseCircle className="w-4 h-4 mt-0.5 text-destructive shrink-0" /> Sua adega foi pausada pela equipe e não aparece no mapa. Fale com a gente pelo e-mail de contato.
          </div>
        )}
        {!adega ? (
          <>
            <p className="text-sm text-muted-foreground mb-5">Coloque sua adega no mapa de Marília. Depois da aprovação do admin, ela aparece pra galera dos rolês perto de você.</p>
            <AdegaForm onSaved={onSaved} />
          </>
        ) : (
          <Tabs defaultValue="produtos">
            <TabsList className="grid grid-cols-2 w-full mb-4">
              <TabsTrigger value="produtos" data-testid="adega-tab-products">Produtos</TabsTrigger>
              <TabsTrigger value="loja" data-testid="adega-tab-store">Loja e horários</TabsTrigger>
            </TabsList>
            <TabsContent value="produtos"><ProductsManager categories={adega.categories} /></TabsContent>
            <TabsContent value="loja"><AdegaForm initial={adega} onSaved={onSaved} /></TabsContent>
          </Tabs>
        )}
      </section>
      <BottomNav />
    </div>
  );
}
